import { useState, useCallback, useRef, useEffect, type ChangeEvent, type RefObject } from 'react';
import type { BankItem } from '../types';

interface UseBankSelectorResult {
  readonly isOpen: boolean;
  readonly searchQuery: string;
  readonly filteredBanks: BankItem[];
  readonly selectedBank: BankItem | null | undefined;
  readonly wrapperRef: RefObject<HTMLDivElement>;
  readonly handleToggle: () => void;
  readonly handleSelect: (bank: BankItem) => void;
  readonly handleSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

export function useBankSelector(
  banks: Array<BankItem>,
  selectedBankId: string,
  onSelect: (bankId: string, bankName: string, bankLogoUrl: string) => void,
  loading: boolean,
): UseBankSelectorResult {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selectedBank = banks.find(b => b.code === selectedBankId);

  const filteredBanks = banks.filter(bank => {
    if (!searchQuery.trim()) return true;
    return bank.name.toLowerCase().includes(searchQuery.toLowerCase().trim());
  });

  const handleToggle = useCallback(() => {
    if (!loading) {
      setIsOpen(prev => !prev);
      setSearchQuery('');
    }
  }, [loading]);

  const handleSelect = useCallback((bank: BankItem) => {
    onSelect(bank.code, bank.name, bank.logo_url || '');
    setIsOpen(false);
    setSearchQuery('');
  }, [onSelect]);

  const handleSearchChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  }, []);

  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (wrapperRef.current && event.target instanceof window.Node && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('click', handleOutsideClick);
    return () => document.removeEventListener('click', handleOutsideClick);
  }, []);

  return {
    isOpen,
    searchQuery,
    filteredBanks,
    selectedBank,
    wrapperRef,
    handleToggle,
    handleSelect,
    handleSearchChange,
  };
}
